import * as React from "react";
import type { EntityKind } from "./types";

export const Icon = {
  Search: (): JSX.Element => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
    </svg>
  ),
  Check: (props: { color?: string } = {}): JSX.Element => (
    <svg viewBox="0 0 24 24" fill="none" stroke={props.color ?? "#fff"} strokeWidth="3"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  ),
  Chevron: (): JSX.Element => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  Back: (): JSX.Element => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  ),
  Close: (): JSX.Element => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  Plus: (): JSX.Element => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  External: (): JSX.Element => (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6" />
      <path d="M20 4L10 14" />
      <path d="M19 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h5" />
    </svg>
  ),
};

/**
 * Per-kind label + emoji. The emoji matches what the backend's
 * HasModelConnections `getModelEmoji()` returns for each model, so a
 * kind-level fallback (e.g. a type-chip selector that doesn't have an
 * entity yet) looks identical to the per-entity emoji that comes back
 * via GraphQL.
 */
export const TYPE_VISUAL: Record<EntityKind, { label: string; emoji: string }> = {
  all: { label: "All", emoji: "🔍" },
  case: { label: "Case", emoji: "📁" },
  task: { label: "Task", emoji: "✅" },
  note: { label: "Note", emoji: "📝" },
  contact: { label: "Contact", emoji: "👤" },
  business: { label: "Business", emoji: "🏢" },
  account: { label: "Account", emoji: "🏦" },
  address: { label: "Address", emoji: "📍" },
  car: { label: "Car", emoji: "🚗" },
  rental: { label: "Rental", emoji: "🚙" },
  asset: { label: "Asset", emoji: "📦" },
  tracking: { label: "Tracking", emoji: "📬" },
  // Generic kinds (minimal Linkable members surfaced by search_all).
  // EntityRow uses TYPE_VISUAL[kind].emoji as a fallback when entity.emoji
  // is missing (it shouldn't be, since the backend's HasModelConnections
  // trait guarantees it).
  trip: { label: "Trip", emoji: "✈️" },
  correspondence: { label: "Correspondence", emoji: "✉️" },
  phone_call: { label: "Phone call", emoji: "📞" },
  sms: { label: "SMS", emoji: "💬" },
  finance_card: { label: "Finance card", emoji: "💳" },
  finance_creditline: { label: "Credit line", emoji: "💰" },
  aircraft: { label: "Aircraft", emoji: "🛩️" },
  legal_entity: { label: "Legal entity", emoji: "🏛️" },
  employment: { label: "Employment", emoji: "👔" },
  tag: { label: "Tag", emoji: "🏷️" },
  pnr: { label: "PNR", emoji: "🎫" },
  calendar_event: { label: "Calendar event", emoji: "📅" },
  medical_encounter: { label: "Medical", emoji: "🩺" },
  // Generic catch-all bucket (see GenericKind "record"). The label is only a
  // fallback — such entities carry a per-entity `typeLabel` (e.g. "Travel
  // credit") and their real emoji comes from the backend via `entity.emoji`.
  record: { label: "Record", emoji: "🔗" },
};
